/**
 * Single-line `Text` entity → `RenderableEntity` adapter (LEGACY string-bag).
 *
 * AutoCAD's `TEXT` entity is the simpler sibling of MTEXT: a single
 * baseline-anchored string with rotation, height, width factor,
 * oblique angle, and a justification grid. The Three.js renderer
 * normalises this into a `text` SceneGeometry payload that the SDF
 * text material consumes via `textRunRenderer.ts` (PR-E2).
 *
 * # Property bag mapping
 *
 *   - `Insertion X / Y / Z`     — baseline insertion point (group
 *     codes 10/20/30 in DXF). NB: the producer `buildTextPreview`
 *     (`dxfGeometry.ts`) writes ONLY `Insertion X` / `Insertion Y` — it
 *     drops z — so `readNumber(.., "Insertion Z")` resolves to 0.
 *   - `Alignment X / Y`         — alignment point for justified
 *     anchors; the producer always writes these (defaulting to the
 *     insertion when the entity has no alignment point), so
 *     `hasAlignmentPoint` is effectively always true on this path.
 *   - `Height`                  — text height in world units.
 *   - `Rotation`                — degrees, CCW positive.
 *   - `Width factor`            — group code 41 (default 1).
 *   - `Oblique angle`           — group code 51 (default 0; degrees).
 *   - `Text style`              — STYLE name (default "Standard").
 *   - `Justify`                 — alignment keyword.
 *   - `Contents` / `entity.label` — string payload.
 *
 * # DEFUSE Direction-3
 *
 * The justify-keyword → `TextAlignment` mapping, the `applyOffset`,
 * the `text` SceneGeometry assembly, and the conservative bbox now live
 * in the shared pure builder `geom/textGeometry.ts` (`buildTextSceneGeometry`),
 * which the typed sibling `typed/textFromEntity.ts` also calls. This adapter
 * keeps ONLY its string-bag value extraction, then delegates to that one
 * truth — behaviour-preserving (golden-parity), no semantic change.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1
 *
 * No simplification: every property the SVG renderer reads is
 * forwarded into the SceneGeometry payload. Annotative scale handling
 * is deferred to PR-E5 (parity with renderText's annotative branch);
 * for PR-E2 the entity's master Height field is taken at face value.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import { readNumber } from "../../scene/renderHelpers";
import type { RenderableEntity } from "../types";

import {
  buildRenderableShell,
  readTextBagPlacement,
  type AdapterContext,
} from "./adapterContext";
import { buildTextSceneGeometry } from "./geom/textGeometry";

/** Adapter for DXF TEXT / `Text` entity type. */
export function textToRenderable(
  entity: EntityPreview,
  ctx: AdapterContext,
): RenderableEntity {
  const props = entity.properties;
  const { insRaw, height, rotation, widthFactor } =
    readTextBagPlacement(props);
  const oblique = readNumber(props, "Oblique angle");
  const styleName = (props["Text style"] ?? "Standard").trim() || "Standard";
  const content = props.Contents ?? entity.label ?? "";
  const justify = props.Justify ?? "Left";
  const hasAlignmentPoint =
    Number.isFinite(Number(props["Alignment X"])) ||
    Number.isFinite(Number(props["Alignment Y"]));
  // Codex #342 F1 — group-71 generation flags (BACKWARD bit 2 / UPSIDE_DOWN bit
  // 4). `buildTextPreview` writes "Generation flags" into the bag; the live mesh
  // path uses it to flip mirrored glyphs. Absent ⇒ 0 ⇒ no flip (golden parity).
  const generationFlags = readNumber(props, "Generation flags", 0);

  const { geometry, bounds } = buildTextSceneGeometry(
    insRaw,
    height,
    rotation,
    widthFactor,
    oblique,
    styleName,
    content,
    justify,
    hasAlignmentPoint,
    generationFlags,
  );

  return buildRenderableShell(entity, ctx, geometry, bounds);
}
